import { PageHeader } from '@/shared/components/ui/PageHeader';
// The "Website" nav section's TDS/MSDS side — every Catalog Product, with its
// TDS/MSDS upload status, in one library instead of opening each product's
// edit form individually. Uploading here matches a website visitor's
// document-request lead to this product by SKU (src/shared/catalog/
// document-fulfillment.js) and auto-emails whichever file is present.

import { useMemo, useState } from 'react';
import { FileArchive, Search, CheckCircle2, XCircle, UploadCloud } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useProducts, useUpdateProduct, type CatalogProduct } from '@/features/catalog/hooks/useCatalogApi';
import { CatalogDocumentSlot, toCatalogDocument } from '@/features/catalog/components/CatalogDocumentSlot';

type Completeness = 'both' | 'partial' | 'none';

function completenessOf(p: CatalogProduct): Completeness {
  const hasTds = !!p.tdsDocument?.storageKey;
  const hasMsds = !!p.msdsDocument?.storageKey;
  if (hasTds && hasMsds) return 'both';
  if (hasTds || hasMsds) return 'partial';
  return 'none';
}

export function WebsiteDocumentsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | Completeness>('all');
  const [uploading, setUploading] = useState<{ id: string; type: 'tds' | 'msds' } | null>(null);

  const products = useProducts({ limit: 500, search: search || undefined });
  const updateProduct = useUpdateProduct();
  const [syncing, setSyncing] = useState(false);

  async function syncToWebsite() {
    setSyncing(true);
    try {
      const { data } = await api.post('/api/catalog/products/website-sync');
      if (data.failed?.length) {
        toast.error(`Synced ${data.synced}/${data.total}. ${data.failed.length} failed — ${data.failed[0]?.error || 'see logs'}`);
      } else {
        toast.success(`Synced ${data.synced}/${data.total} product(s) to the website.`);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Sync failed — is WEBSITE_CATALOG_WEBHOOK_URL configured on the server?'));
    } finally {
      setSyncing(false);
    }
  }

  const rows = useMemo(() => {
    const all = products.data?.data ?? [];
    if (filter === 'all') return all;
    return all.filter((p) => completenessOf(p) === filter);
  }, [products.data, filter]);

  const summary = useMemo(() => {
    const all = products.data?.data ?? [];
    return {
      total: all.length,
      both: all.filter((p) => completenessOf(p) === 'both').length,
      partial: all.filter((p) => completenessOf(p) === 'partial').length,
      none: all.filter((p) => completenessOf(p) === 'none').length,
    };
  }, [products.data]);

  async function upload(product: CatalogProduct, docType: 'tds' | 'msds', file: File) {
    setUploading({ id: product._id, type: docType });
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('category', 'catalog-document');
      const { data } = await api.post('/api/files/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      const field = docType === 'tds' ? 'tdsDocument' : 'msdsDocument';
      await updateProduct.mutateAsync({
        id: product._id,
        [field]: { storageKey: data.storageKey, originalname: data.originalName, mimeType: data.mimeType, size: data.size, uploadedAt: data.uploadedAt },
      });
      toast.success(`${docType.toUpperCase()} uploaded for ${product.name}.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Failed to upload ${docType.toUpperCase()}.`));
    } finally {
      setUploading(null);
    }
  }

  async function clear(product: CatalogProduct, docType: 'tds' | 'msds') {
    const field = docType === 'tds' ? 'tdsDocument' : 'msdsDocument';
    try {
      await updateProduct.mutateAsync({ id: product._id, [field]: { storageKey: '', originalname: '', mimeType: '', size: 0, uploadedAt: null } });
      toast.success(`${docType.toUpperCase()} removed.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to remove.'));
    }
  }

  const FILTERS: Array<{ key: 'all' | Completeness; label: string; count: number }> = [
    { key: 'all', label: 'All products', count: summary.total },
    { key: 'both', label: 'TDS + MSDS complete', count: summary.both },
    { key: 'partial', label: 'One missing', count: summary.partial },
    { key: 'none', label: 'Neither uploaded', count: summary.none },
  ];

  return (
    <div className="max-w-[1580px] mx-auto space-y-4 pb-16">
      <PageHeader
        icon={FileArchive}
        eyebrow="Marketing"
        title="TDS / MSDS Library"
        description="Upload once here; a website visitor's document request auto-emails whichever file is present, matched by SKU."
        actions={
            <Button variant="secondary" size="sm" onClick={syncToWebsite} disabled={syncing}>
              <UploadCloud className="h-4 w-4 mr-1.5" />
              {syncing ? 'Syncing…' : 'Sync catalogue to website'}
            </Button>
        }
      />
      <Card>
        <CardBody className="space-y-3">
          <div className="max-w-sm">
            <Input
              placeholder="Search by product name or SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`rounded-full border px-3 py-1.5 text-body-xs font-medium transition-colors ${
                  filter === f.key
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-outline-variant text-on-surface-variant hover:bg-surface-container-high/40'
                }`}
              >
                {f.label} <span className="ml-1 opacity-70">({f.count})</span>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {products.isLoading ? (
            <div className="p-4"><Skeleton className="h-64" /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Search className="h-6 w-6" />}
              title="No products match"
              description="Try a different search or filter."
            />
          ) : (
            <div className="divide-y divide-outline-variant/60">
              {rows.map((p) => {
                const completeness = completenessOf(p);
                return (
                  <div key={p._id} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex items-center gap-2 md:w-64 shrink-0">
                      {completeness === 'both' ? (
                        <CheckCircle2 className="h-4 w-4 text-success-text shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-warning-text shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-on-surface truncate">{p.name}</p>
                        <p className="text-body-xs text-on-surface-variant">{p.sku || 'No SKU set'}</p>
                      </div>
                    </div>
                    {!p.sku && (
                      <p className="text-body-xs text-warning-text md:hidden">Set a SKU on this product first — a website request matches by SKU.</p>
                    )}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <CatalogDocumentSlot
                        label="Technical Data Sheet (TDS)"
                        doc={toCatalogDocument(p.tdsDocument)}
                        uploading={uploading?.id === p._id && uploading.type === 'tds'}
                        onUpload={(file) => upload(p, 'tds', file)}
                        onClear={() => clear(p, 'tds')}
                      />
                      <CatalogDocumentSlot
                        label="Material Safety Data Sheet (MSDS)"
                        doc={toCatalogDocument(p.msdsDocument)}
                        uploading={uploading?.id === p._id && uploading.type === 'msds'}
                        onUpload={(file) => upload(p, 'msds', file)}
                        onClear={() => clear(p, 'msds')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
